import React from 'react';
import { aboutContent, whyDubai } from '../data/mock';
import { Sparkles, Target, Heart, Lightbulb } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-purple-600 via-purple-700 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <span className="text-sm text-white/90">About Us</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-extralight mb-6 tracking-tight">
            {aboutContent.title}
          </h1>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-2xl md:text-3xl font-light text-gray-800 leading-relaxed mb-12">
            {aboutContent.intro}
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            {aboutContent.mission}
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            {aboutContent.philosophy}
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-light text-center mb-20 tracking-tight">
            Our <span className="text-purple-600">Core Values</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-light mb-4">Purpose-Driven</h3>
              <p className="text-gray-600 leading-relaxed">
                Every innovation we create serves a meaningful purpose — to simplify complexity and empower human potential through intelligent technology.
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-light mb-4">Human-Centered</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe technology should adapt to humans, not the other way around. Our platforms are designed with elegance, intuition, and empathy.
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-light mb-4">Innovation Always</h3>
              <p className="text-gray-600 leading-relaxed">
                We don't follow trends — we create them. Our commitment to continuous innovation keeps us years ahead in the AI and SaaS landscape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Movement */}
      <section className="py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-5xl font-light mb-12 tracking-tight text-center">
            More Than a Company — <span className="text-purple-600">A Movement</span>
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            {aboutContent.movement}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {aboutContent.invitation}
          </p>
        </div>
      </section>

      {/* Why Dubai */}
      <section id="dubai" className="py-32 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-light mb-6 tracking-tight">
                {whyDubai.title}
              </h2>
              <p className="text-lg text-white/80 leading-relaxed mb-8">
                {whyDubai.content}
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                  <p className="text-white/70">Digital-first governance and smart city initiatives</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                  <p className="text-white/70">Hub for AI and technological innovation</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                  <p className="text-white/70">Global ecosystem for startups and visionaries</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                  <p className="text-white/70">World-class infrastructure and business environment</p>
                </div>
              </div>
            </div>
            <div className="relative h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={whyDubai.image}
                alt="Dubai Skyline"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;